import type { KeyboardEvent } from 'react';
import { GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from '../icon-button/icon-button.tsx';
import { TemplateListRow, type TemplateListRowProps } from './template-list-row.tsx';

export interface SortableTemplateListRowProps extends Omit<
  TemplateListRowProps,
  'handle' | 'ref' | 'dragTransform' | 'dragTransition' | 'dragging'
> {
  /* One place up or down the list, from the keyboard. */
  onMove: (direction: -1 | 1) => void;
}

/**
 * The same row, in a list somebody arranges. Only the grip drags, so the
 * row's own buttons never hesitate. The arrows on the grip move one place in
 * the list: dnd-kit's keyboard sensor scrolls the page instead on a long list.
 */
export function SortableTemplateListRow({
  template,
  onMove,
  ...props
}: SortableTemplateListRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: template.id });

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    /* Or the page scrolls away under the row that was just moved. */
    event.preventDefault();
    onMove(event.key === 'ArrowUp' ? -1 : 1);
  }

  return (
    <TemplateListRow
      template={template}
      {...props}
      ref={setNodeRef}
      dragTransform={CSS.Translate.toString(transform) ?? undefined}
      dragTransition={transition}
      dragging={isDragging}
      handle={
        <IconButton
          variant='quiet'
          shape='square'
          className='cursor-grab'
          aria-label={props.labels.move({ name: template.name })}
          {...attributes}
          {...listeners}
          /* After the library's own, so the arrows are ours to answer. */
          onKeyDown={handleKeyDown}
        >
          <GripVertical className='size-4' aria-hidden='true' />
        </IconButton>
      }
    />
  );
}
